import { observer } from "mobx-react-lite";
import { useMemo, useRef } from "react";

import { parseDatetimeToLocal } from "@/utils/utils";

import { useFormFieldId } from "../form/form-field-id-context";
import { BaseInput } from "./base-input";
import { FieldProps, useField, useModelBinding } from "./utils";
import { ValidationTooltip } from "./validation-tooltip";

interface Props<M extends object> extends FieldProps<M, string | null> {
  type: "datetime-local" | "date";
  required?: boolean;
  disabled?: boolean;
}

export const DateInput = observer(function DateInput<M extends object>({
  type,
  required,
  disabled,
  ...restProps
}: Props<M>) {
  const formFieldId = useFormFieldId();
  const elementRef = useRef<HTMLInputElement>(null);

  const { value, onChange } = useModelBinding(restProps);
  const { isInvalid, tooltip } = useField(elementRef, value);

  const displayValue = useMemo(() => {
    if (value && !isLocal(value)) {
      return parseDatetimeToLocal(value);
    }
    return value ?? "";
  }, [value]);

  return (
    <ValidationTooltip model={tooltip}>
      <BaseInput
        id={formFieldId}
        type={type}
        step="1"
        value={displayValue}
        onChange={(event) => {
          onChange(
            type === "datetime-local" && !!event.target.value
              ? new Date(event.target.value).toISOString()
              : event.target.value || null,
          );
        }}
        required={required}
        className="w-full min-w-[200px]"
        aria-invalid={isInvalid}
        ref={elementRef}
        disabled={disabled}
      />
    </ValidationTooltip>
  );
});

function isLocal(str: Date | string) {
  return typeof str === "string" && str.length > 0 && !str.includes("Z");
}
